import {
  Typography,
  Dialog,
  DialogContent,
  Paper,
  IconButton,
  InputBase,
  Divider,
  List,
  ListItemButton,
  Box,
  Skeleton,
  Chip,
  Avatar,
  InputAdornment,
  CircularProgress,
  Fade,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import { LuSearchX } from "react-icons/lu";
import { IoSearchSharp, IoCloseCircle } from "react-icons/io5";
import { HiOutlineSparkles } from "react-icons/hi2";
import dayjs from "dayjs";
import taskApi from "../../../api/endpoints/task";

// Visual identity per task type — icon color + short label used for the
// round avatar and the left accent bar on each result row.
const TYPE_META = {
  1: { color: "#1976d2", label: "مشترك" }, // مشترك جديد
  2: { color: "#ed6c02", label: "كابينة" }, // صيانة كابينة
  3: { color: "#ed6c02", label: "صيانة" }, // صيانة مشترك
  4: { color: "#9c27b0", label: "عمود" }, // صيانة عمود
  5: { color: "#2e7d32", label: "تجديد" }, // طلب تجديد
  6: { color: "#d32f2f", label: "تصفية" }, // تصفية أعمدة
  7: { color: "#757575", label: "أخرى" }, // غير محدد
};
const DEFAULT_META = { color: "#616161", label: "مهمة" };

const getTypeMeta = (typeId) => TYPE_META[Number(typeId)] || DEFAULT_META;

const getTaskRouteToOpen = (item) => {
  const typeId = Number(item?.type_id);
  const taskId = item?.id;
  if (!taskId) return "/task";

  switch (typeId) {
    case 1:
      return `/task/${taskId}`;
    case 2:
    case 3:
      return `/maintenance-task/${taskId}`;
    case 4:
      return `/poles-maintenance-task/${taskId}`;
    case 5:
      return `/subscription-request-task/${taskId}`;
    case 6:
      return `/fat-clearance-task/${taskId}`;
    case 7:
      return `/unknown-task/${taskId}`;
    default:
      return `/task/${taskId}`;
  }
};

const MIN_QUERY_LENGTH = 3;

const GlobalSearch = () => {
  const [dialog, setDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("idle");
  const [tasksData, setTasksData] = useState([]);
  const inputRef = useRef(null);

  const openDialog = () => setDialog(true);
  const closeDialog = () => {
    setDialog(false);
    setSearch("");
    setTasksData([]);
    setStatus("idle");
  };

  const fetchSearchTasks = useCallback(async (query) => {
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setTasksData([]);
      setStatus("idle");
      return;
    }
    setStatus("loading");
    try {
      const res = await taskApi.globalSearch({ search: query });
      const data = res.data?.data || res.data || [];
      setTasksData(Array.isArray(data) ? data : []);
      setStatus("done");
    } catch (error) {
      setTasksData([]);
      setStatus("error");
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      fetchSearchTasks(search);
    }
  };

  const handleClear = () => {
    setSearch("");
    setTasksData([]);
    setStatus("idle");
    inputRef.current?.focus();
  };

  const ResultSkeleton = () => (
    <Box>
      {Array.from(new Array(5)).map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.1 }}>
          <Skeleton variant="circular" width={34} height={34} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="45%" height={20} />
            <Skeleton variant="text" width="65%" height={16} />
          </Box>
          <Skeleton variant="rounded" width={54} height={20} />
        </Box>
      ))}
    </Box>
  );

  const EmptyState = ({ icon, title, subtitle }) => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 6, opacity: 0.75 }}>
      {icon}
      <Typography variant="body1" fontWeight={600} color="text.secondary" mt={1.5}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.disabled" mt={0.5}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const ResultRow = ({ item }) => {
    const meta = getTypeMeta(item.type_id);
    const targetUrl = getTaskRouteToOpen(item);
    const customerName = item.name || "--";
    const mobile = item.mobile;
    const location = [item.cabinet_name, item.pole_name].filter(Boolean).join(" | ");

    return (
      <ListItemButton
        onClick={() => window.open(targetUrl, "_blank")}
        sx={{
          alignItems: "flex-start",
          gap: 1.5,
          px: 2,
          py: 1.1,
          borderLeft: "3px solid transparent",
          "&:hover": {
            borderLeftColor: meta.color,
            bgcolor: "action.hover",
          },
        }}
      >
        <Typography variant="caption" color="text.disabled">
          {item.id}
        </Typography>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} noWrap>
              {customerName}
            </Typography>

            {mobile && (
              <Typography variant="caption" color="text.secondary" dir="ltr">
                {mobile}
              </Typography>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={0.75} mt={0.25} flexWrap="wrap">
            <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
              {item.type_ar || meta.label}
            </Typography>
            {item.city_name && (
              <>
                <Typography variant="caption" color="text.disabled">·</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {item.city_name}
                </Typography>
              </>
            )}
            {location && (
              <>
                <Typography variant="caption" color="text.disabled">·</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {location}
                </Typography>
              </>
            )}

          </Box>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
          {item.status_ar && (
            <Chip
              size="small"
              variant="outlined"
              label={item.status_ar}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                borderColor: meta.color,
                color: meta.color,
              }}
            />
          )}
          {item.created_at && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
              {dayjs(item.created_at).format("YYYY/MM/DD HH:mm")}
            </Typography>
          )}
        </Box>
      </ListItemButton>
    );
  };

  return (
    <>
      <Paper
        sx={{
          p: "0px 4px",
          mx: { xs: 0.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          width: { xs: 130, sm: 180, md: 220 },
          cursor: "pointer",
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          transition: "border-color .15s ease, background-color .15s ease",
        }}
        onClick={openDialog}
      >
        <IconButton sx={{ p: "5px" }} aria-label="search" tabIndex={-1}>
          <IoSearchSharp size={16} />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          البحث العام...
        </Typography>
      </Paper>

      <Dialog
        open={dialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        TransitionComponent={Fade}
        transitionDuration={150}
        PaperProps={{
          dir: "rtl",
          sx: {
            mt: { xs: "8vh", sm: "10vh" },
            mx: { xs: 1, sm: 2 },
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            height: 56,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <IoSearchSharp size={18} color="#1976d2" />
          <InputBase
            inputRef={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            fullWidth
            placeholder="ابحث بالاسم، الهاتف، رقم المهمة..."
            inputProps={{ style: { fontSize: 15 } }}
            endAdornment={
              status === "loading" ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} thickness={5} />
                </InputAdornment>
              ) : (
                search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear} edge="end">
                      <IoCloseCircle size={17} />
                    </IconButton>
                  </InputAdornment>
                )
              )
            }
          />
        </Box>

        <DialogContent dir="rtl" sx={{ p: 0 }}>
          <Box sx={{ maxHeight: { xs: "65vh", sm: "480px" }, overflowY: "auto" }}>
            {status === "loading" && tasksData.length === 0 && <ResultSkeleton />}

            {status !== "loading" && search.trim().length < MIN_QUERY_LENGTH && (
              <EmptyState
                icon={<HiOutlineSparkles size={40} color="#bdbdbd" />}
                title="ابدأ الكتابة للبحث"
                subtitle="بثلاث حروف على الأقل — بالاسم، الهاتف، أو رقم المهمة"
              />
            )}

            {status === "done" && tasksData.length === 0 && search.trim().length >= MIN_QUERY_LENGTH && (
              <EmptyState icon={<LuSearchX size={40} color="#bdbdbd" />} title="لا توجد نتائج مطابقة" />
            )}

            {status === "error" && (
              <EmptyState
                icon={<LuSearchX size={40} color="#d32f2f" />}
                title="تعذر إتمام البحث"
                subtitle="حاول مرة أخرى"
              />
            )}

            {tasksData.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, pt: 1.5, pb: 0.5, display: "block" }}
                >
                  {tasksData.length} نتيجة
                </Typography>
                <List disablePadding>
                  {tasksData.map((item, index) => (
                    <Box key={item.id ?? index}>
                      <ResultRow item={item} />
                      {index < tasksData.length - 1 && <Divider component="li" sx={{ mx: 2 }} />}
                    </Box>
                  ))}
                </List>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;